import React from 'react';

const IncidentSummary = ({ incidentData, catalogs, assignedTo, users = [] }) => {
  const { categories = [], channels = [] } = catalogs || {};
  const safeIncidentData = incidentData || {};
  
  // Obtener el nombre del responsable asignado
  const getAssignedUserName = (userId) => {
    if (!userId) return 'No asignado';
    const user = users.find(u => u.id === userId || u.id === String(userId));
    if (user) {
      return `${user.first_name} ${user.last_name}${user.email ? ` (${user.email})` : ''}`;
    }
    return 'Usuario no encontrado';
  };

  // Helper functions para obtener nombres de los catálogos
  // Usar los nombres guardados directamente si están disponibles, sino buscar en los catálogos
  const getCategoryName = (id) => {
    // Primero intentar usar el nombre guardado
    if (safeIncidentData.category_name) {
      return safeIncidentData.category_name;
    }
    // Si no, buscar en el catálogo
    const category = categories.find(c => c.id === id);
    return category ? category.name : 'No especificado';
  };

  const getFormName = () => {
    return safeIncidentData.form_name || 'No especificado';
  };

  const getChannelName = (id) => {
    const channel = channels.find(c => c.id === id);
    return channel ? channel.name : 'No especificado';
  };

  return (
    <div className="incident-summary">
      <div className="summary-header">
        <h4>Resumen del Incidente</h4>
        <p>Revise la información antes de crear el incidente</p>
      </div>

      <div className="summary-sections">
        {/* Información del Contacto */}
        <div className="summary-section">
          <div className="section-header">
            <h5>👤 Información del Contacto</h5>
          </div>
          <div className="section-content">
            {safeIncidentData.selectedContact ? (
              <div className="contact-summary">
                <div className="summary-item">
                  <span className="label">Nombre:</span>
                  <span className="value">{safeIncidentData.selectedContact.first_name || 'No especificado'}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Apellido:</span>
                  <span className="value">{safeIncidentData.selectedContact.last_name || 'No especificado'}</span>
                </div>
                <div className="summary-item">
                  <span className="label">CUIL:</span>
                  <span className="value">{safeIncidentData.selectedContact.cuil || 'No especificado'}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Email:</span>
                  <span className="value">{safeIncidentData.selectedContact.email || 'No especificado'}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Teléfono 1:</span>
                  <span className="value">{safeIncidentData.selectedContact.phone || 'No especificado'}</span>
                </div>
                {safeIncidentData.selectedContact.phone2 && (
                  <div className="summary-item">
                    <span className="label">Teléfono 2:</span>
                    <span className="value">{safeIncidentData.selectedContact.phone2}</span>
                  </div>
                )}
                {safeIncidentData.selectedContact.address && (
                  <div className="summary-item">
                    <span className="label">Dirección:</span>
                    <span className="value">{safeIncidentData.selectedContact.address}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="no-contact">
                <span className="warning-text">⚠️ No se ha seleccionado un contacto</span>
              </div>
            )}
          </div>
        </div>

        {/* Clasificación */}
        <div className="summary-section">
          <div className="section-header">
            <h5>🏷️ Clasificación</h5>
          </div>
          <div className="section-content">
            <div className="summary-item">
              <span className="label">Categoría:</span>
              <span className="value">{getCategoryName(safeIncidentData.category_id)}</span>
            </div>
            <div className="summary-item">
              <span className="label">Formulario:</span>
              <span className="value">{getFormName()}</span>
            </div>
            <div className="summary-item">
              <span className="label">Canal:</span>
              <span className="value">{getChannelName(safeIncidentData.channel_id)}</span>
            </div>
          </div>
        </div>

        {/* Ubicación (si es requerida) */}
        {safeIncidentData.requiresLocation && safeIncidentData.location?.route && (
          <div className="summary-section">
            <div className="section-header">
              <h5>📍 Ubicación</h5>
            </div>
            <div className="section-content">
              <div className="summary-item">
                <span className="label">Ruta:</span>
                <span className="value">{safeIncidentData.location.route}</span>
              </div>
              {safeIncidentData.location.section && (
                <div className="summary-item">
                  <span className="label">Sección/Tramo:</span>
                  <span className="value">{safeIncidentData.location.section}</span>
                </div>
              )}
              <div className="summary-item">
                <span className="label">Kilómetro:</span>
                <span className="value">{safeIncidentData.location.km}</span>
              </div>
              <div className="summary-item">
                <span className="label">Sentido:</span>
                <span className="value">{safeIncidentData.location.direction}</span>
              </div>
              {safeIncidentData.location.reference && (
                <div className="summary-item">
                  <span className="label">Referencia:</span>
                  <span className="value">{safeIncidentData.location.reference}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {safeIncidentData.title && (
          <div className="summary-section">
            <div className="section-header">
              <h5>💬 Título</h5>
            </div>
            <div className="section-content">
              <div className="title-summary">
                <p>{safeIncidentData.title}</p>
              </div>
            </div>
          </div>
        )}

        {safeIncidentData.description && (
          <div className="summary-section">
            <div className="section-header">
              <h5>💬 Descripción</h5>
            </div>
            <div className="section-content">
              <div className="description-summary">
                <p>{safeIncidentData.description}</p>
              </div>
            </div>
          </div>
        )}

        {/* Responsable Asignado */}
        <div className="summary-section">
          <div className="section-header">
            <h5>👤 Responsable Asignado</h5>
          </div>
          <div className="section-content">
            <div className="summary-item">
              <span className="label">Responsable:</span>
              <span className="value">{getAssignedUserName(assignedTo)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Información adicional */}
      <div className="summary-footer">
        <div className="creation-info">
          <p><strong>Fecha de creación:</strong> {new Date().toLocaleString('es-AR')}</p>
          <p><strong>Estado inicial:</strong> Nuevo</p>
        </div>
      </div>
    </div>
  );
};

export default IncidentSummary;
